import React, { useEffect, useState } from 'react';
import {
  Modal,
  Group,
  Button,
  Stack,
  Text,
  Table,
  Title,
  LoadingOverlay,
  TextInput,
  Badge,
  Checkbox,
  Avatar
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { IconSearch } from '@tabler/icons-react';
import { Participant } from 'pages/app/LIA/AssessmentSchedules/utils/schema';
import { dataBinusService } from 'services/dataBinusService';
import useDebounce from 'hooks/useDebounce';

/* eslint-disable */
interface SmeSelectionModalProps {
  opened: boolean;
  onClose: () => void;
  existingSmes: Participant[];
  onSubmit: (selectedSmes: Participant[]) => void;
}
/* eslint-enable */

const SmeSelectionModal: React.FC<SmeSelectionModalProps> = ({
  opened,
  onClose,
  existingSmes,
  onSubmit
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSmes, setSelectedSmes] = useState<Participant[]>([]);
  const [searchEmployee, setSearchEmployee] = useState(true);
  const [searchLecturer, setSearchLecturer] = useState(true);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  useEffect(() => {
    if (opened) {
      setSelectedSmes([]);
      setSearchQuery('');
    }
  }, [opened]);

  const { data: emplecData, isLoading: isLoadingEmplec } = useQuery({
    queryKey: ['listEmplec', debouncedSearchQuery, searchEmployee, searchLecturer],
    queryFn: () =>
      dataBinusService.getEmplecList({
        search: debouncedSearchQuery,
        isEmployee: searchEmployee,
        isLecturer: searchLecturer
      }),
    enabled: opened && debouncedSearchQuery.length > 2
  });

  const handleToggleSme = (sme: Participant) => {
    setSelectedSmes((prev) => {
      const isSelected = prev.some((s) => s.binusianId === sme.binusianId);

      if (isSelected) {
        return prev.filter((s) => s.binusianId !== sme.binusianId);
      } else {
        return [...prev, { ...sme, type: 'SME' }];
      }
    });
  };

  const handleSubmit = () => {
    onSubmit(selectedSmes);
  };

  const employeeParticipants: Participant[] = [];
  if (emplecData?.employees?.data) {
    emplecData.employees.data.forEach((emp: any) => {
      employeeParticipants.push({
        binusianId: emp.binusianId,
        name: emp.name,
        division: emp.bu || 'Employee',
        type: 'SME',
        email: emp.email
      });
    });
  }

  const lecturerParticipants: Participant[] = [];
  if (emplecData?.lecturers?.data) {
    emplecData.lecturers.data.forEach((lec: any) => {
      lecturerParticipants.push({
        binusianId: lec.binusianId,
        name: lec.name,
        division: lec.fakultas || 'Lecturer',
        type: 'SME',
        email: lec.email
      });
    });
  }

  const isExistingSme = (sme: Participant) => {
    return existingSmes.some((s) => s.binusianId === sme.binusianId);
  };

  return (
    <Modal opened={opened} centered onClose={onClose} size='xl' withCloseButton={false}>
      <Stack gap='md' p={16}>
        <Title order={3}>Select Subject Matter Experts (SMEs)</Title>

        <Group mb='md'>
          <Text fw={500}>Search In:</Text>
          <Checkbox
            label='Employees'
            checked={searchEmployee}
            onChange={(e) => setSearchEmployee(e.currentTarget.checked)}
          />
          <Checkbox
            label='Lecturers'
            checked={searchLecturer}
            onChange={(e) => setSearchLecturer(e.currentTarget.checked)}
          />
        </Group>

        <TextInput
          label='Search SMEs'
          placeholder='Search by name or ID...'
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          rightSection={<IconSearch size={16} />}
        />

        <div style={{ position: 'relative', minHeight: '300px' }}>
          <LoadingOverlay visible={isLoadingEmplec && debouncedSearchQuery.length > 2} />

          {debouncedSearchQuery.length > 2 && !isLoadingEmplec ? (
            <Stack mt='md'>
              {searchEmployee && employeeParticipants.length > 0 && (
                <>
                  <Badge color='blue' size='lg' variant='light'>
                    Employees
                  </Badge>
                  <Table striped border={1} mt='md'>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Division</Table.Th>
                        <Table.Th style={{ width: 80 }}>Select</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {employeeParticipants.map((sme) => {
                        const isExisting = isExistingSme(sme);
                        return (
                          <Table.Tr key={sme.binusianId} opacity={isExisting ? 0.5 : 1}>
                            <Table.Td>
                              <Group>
                                <Avatar color='#7B2183' radius='xl'>
                                  {sme.name && sme.name.length > 0 ? sme.name.charAt(0) : '?'}
                                </Avatar>
                                <div>
                                  <Text size='sm' fw={500}>
                                    {sme.name || 'Unknown Name'}
                                  </Text>
                                  <Text size='xs' c='dimmed'>
                                    {sme.binusianId}
                                  </Text>
                                  {isExisting && (
                                    <Text size='xs' c='red'>
                                      Already added
                                    </Text>
                                  )}
                                </div>
                              </Group>
                            </Table.Td>
                            <Table.Td>{sme.email || 'N/A'}</Table.Td>
                            <Table.Td>{sme.division || 'N/A'}</Table.Td>
                            <Table.Td>
                              <Checkbox
                                disabled={isExisting}
                                checked={
                                  isExisting ||
                                  selectedSmes.some((s) => s.binusianId === sme.binusianId)
                                }
                                onChange={() => !isExisting && handleToggleSme(sme)}
                              />
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </>
              )}

              {searchLecturer && lecturerParticipants.length > 0 && (
                <>
                  <Badge color='green' size='lg' variant='light' mt='md'>
                    Lecturers
                  </Badge>
                  <Table striped border={1} mt='md'>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Division</Table.Th>
                        <Table.Th style={{ width: 80 }}>Select</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {lecturerParticipants.map((sme) => {
                        const isExisting = isExistingSme(sme);
                        return (
                          <Table.Tr key={sme.binusianId} opacity={isExisting ? 0.5 : 1}>
                            <Table.Td>
                              <Group>
                                <Avatar color='#7B2183' radius='xl'>
                                  {sme.name && sme.name.length > 0 ? sme.name.charAt(0) : '?'}
                                </Avatar>
                                <div>
                                  <Text size='sm' fw={500}>
                                    {sme.name || 'Unknown Name'}
                                  </Text>
                                  <Text size='xs' c='dimmed'>
                                    {sme.binusianId}
                                  </Text>
                                  {isExisting && (
                                    <Text size='xs' c='red'>
                                      Already added
                                    </Text>
                                  )}
                                </div>
                              </Group>
                            </Table.Td>
                            <Table.Td>{sme.email || 'N/A'}</Table.Td>
                            <Table.Td>{sme.division || 'N/A'}</Table.Td>
                            <Table.Td>
                              <Checkbox
                                disabled={isExisting}
                                checked={
                                  isExisting ||
                                  selectedSmes.some((s) => s.binusianId === sme.binusianId)
                                }
                                onChange={() => !isExisting && handleToggleSme(sme)}
                              />
                            </Table.Td>
                          </Table.Tr>
                        );
                      })}
                    </Table.Tbody>
                  </Table>
                </>
              )}

              {employeeParticipants.length === 0 && lecturerParticipants.length === 0 && (
                <Text c='dimmed' ta='center' mt='md'>
                  No SMEs found matching your search
                </Text>
              )}
            </Stack>
          ) : (
            <Text c='dimmed' ta='center' mt='md'>
              {debouncedSearchQuery.length > 0
                ? isLoadingEmplec
                  ? 'Searching...'
                  : 'No SMEs found matching your search'
                : 'Enter at least 3 characters to search'}
            </Text>
          )}
        </div>

        <Group justify='flex-end' mt='md'>
          <Button variant='outline' onClick={onClose} color='#7B2183'>
            Cancel
          </Button>
          <Button onClick={handleSubmit} color='#7B2183' disabled={selectedSmes.length === 0}>
            Add {selectedSmes.length} SME{selectedSmes.length !== 1 ? 's' : ''}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};

export default SmeSelectionModal;
